import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import { Observable, Subject, merge, of } from "rxjs";
import {
    catchError,
    filter,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, VirkeskopKontraktstatus } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KontraktVacsStatusDTO } from "../../dto/vacs/vacs-dto";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { AvtalService } from "../../services/avtal.service";

@Component({
    selector: "rask-kontrakt-vacs",
    templateUrl: "./kontrakt-vacs.component.html",
    styleUrls: ["./kontrakt-vacs.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgbTooltip]
})
export class KontraktVacsComponent implements OnInit, OnDestroy {
    protected hasError = false;
    protected vacsStatus?: KontraktVacsStatusDTO;

    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly destroy$ = new Subject<void>();
    private readonly modalService = inject(RaskModalService);

    private avtalDirty = false;
    private kontrakt: EntityDTO;

    protected get buttonText() {
        return this.vacsStatus.sentToVacs
            ? "Uppdatera VACS"
            : "Skicka till VACS";
    }

    protected get canUpdateVacs() {
        return this.vacsStatus.canUpdate
            && !this.avtalDirty
            && this.vacsStatus.viol3;
    }

    protected get isKontrakt() {
        return this.vacsStatus?.kontraktStatus === VirkeskopKontraktstatus.Kontrakt;
    }

    protected get lastUpdated() {
        return this.vacsStatus.lastUpdated;
    }

    protected get readonly() {
        return this.vacsStatus?.kontraktStatus === VirkeskopKontraktstatus.Makulerat;
    }

    protected get tooltipText(): string {
        if (!this.vacsStatus.viol3) {
            return "Sändningar till Vacs viol2 stöds ej längre";
        }
        if (this.hasError) {
            return "Det gick inte att skicka kontraktet till VACS";
        }
        if (this.lastUpdated) {
            return `Skickades senast ${moment(this.lastUpdated).format("LLL")}`;
        }
        return "";
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.avtalMediatorService.dirty$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(dirty => this.avtalDirty = dirty);

        const loadData = (kontrakt: EntityDTO): Observable<KontraktVacsStatusDTO> => {
            this.kontrakt = kontrakt;
            this.vacsStatus = null;
            this.hasError = false;

            if (!ObjectUtils.hasValue(kontrakt?.id)) {
                return of(null);
            }

            return this.avtalService.getVacsStatus(kontrakt.id);
        };

        const loadInitialData$ = this.avtalMediatorService.activeKontrakt$
            .pipe(switchMap((k) => loadData(k)));
        const reloadDataOnSave$ = this.avtalMediatorService.save$
            .pipe(switchMap(() => loadData(this.kontrakt)));
        const reloadDataOnBetalRaderSaved$ = this.avtalMediatorService.onSkogsvardBetalraderSaved$
            .pipe(switchMap(() => loadData(this.kontrakt)));
        const reloadDataOnAtgardSlutjusterad$ = this.avtalMediatorService.onAtgardSlutjusterad$
            .pipe(switchMap(() => loadData(this.kontrakt)));

        const loadData$ = merge(loadInitialData$, reloadDataOnSave$, reloadDataOnBetalRaderSaved$, reloadDataOnAtgardSlutjusterad$)
            .pipe(filter(k => ObjectUtils.hasValue(k)));

        loadData$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(status => {
            this.vacsStatus = status;
            this.hasError = false;
        });
    }

    protected updateVacs() {
        if (!this.vacsStatus?.canUpdate || this.readonly) {
            return;
        }

        const sendUpdate = () => {
            this.vacsStatus.canUpdate = false;
            this.avtalService.updateVacs(this.kontrakt.id).pipe(
                tap(status => this.vacsStatus = status),
                catchError(err => {
                    this.hasError = true;
                    return err;
                }),
                switchMap(() => this.avtalMediatorService.reloadAvtal())
            ).subscribe();
        };

        if (!this.vacsStatus.avtalSentToVacs) {
            this.modalService
                .confirm(new ModalSettings(
                    `Skicka kontrakt ${this.vacsStatus.kontraktBenamning} till VACS`,
                    `Leverantörer för något av kontrakten i avtalet kommer inte kunna uppdateras om du skickar detta till VACS. Vill du fortsätta?`))
                .subscribe(response => {
                    if (response === ConfirmDialogResponse.save) {
                        sendUpdate();
                    }
                });
        } else {
            sendUpdate();
        }
    }
}
